import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';

import InfiniteScroll from "src/components/infiniteScroll";
import Slider from "src/components/slider/index";
import { TabBar } from "src/components/tabbar";
import PlaceholderEmptyMessage from 'src/components/placeholderEmptyMessage'
import TimeAgo from "react-timeago";

import { fetchNotifications, deleteAllNotifications, updatingNotificationsAsRead } from "src/redux/actions/notifications";
import { Notification } from "src/types/hub";


import moment from "moment";

import _ from "lodash";
import cx from "classnames";
import styles from "./notificationSlider.module.scss";
import DOMPurify from "dompurify";
import { useHubAdoption } from "src/redux/actions/adoption/hub";
import Text from "src/components/text";

const NotificationEmptyMessage = '/images/assets/notification_empty_message.svg'
const defaultIcon = '/images/defaultIcons/default_asset.jpg';

interface NotificationSliderProps {
  show: boolean;
  onClose: () => void;
}

const groupNotificationsByDate = (notifications: Notification[]) => {
  return _.groupBy(notifications, (notification: Notification) => {
    const createdDate = moment(notification.createdDate);
    const today = moment();

    if (createdDate.isSame(today, 'day')) {
      return 'Today';
    }

    if (today.week() - createdDate.week() === 0) {
      return createdDate.format('dddd'); 
    }

    if (today.week() - createdDate.week() === 1) {
      return 'Last Week';
    }

    return 'Older';
  });
};

const renderGroupedNotifications = (notifications: Notification[], groupTitle: string) => {
  return (
    <div className={styles.groupNotificationContainer}>
      <div className={styles.groupContainer}>
        <h1 className={styles.groupTitle}>{groupTitle}</h1>
        <div className={styles.line}></div>
      </div>
      {_.size(notifications) > 0 && (
        <ul className={styles.notificationsList}>
          {notifications.map(notification => {
            const icon = _.get(notification, 'icon.thumbnail') || defaultIcon;
            const key = `${_.get(notification, 'id')}_${_.get(notification, 'date')}`;
            const hasMore = notification.totalEvents - 1 > 0;

            const notificationItem = (
            <li key={key}>
                <div className={styles.itemContent}>
                  {!_.get(notification, 'read') && (<div className={styles.readAsset} />)}
                  <img src={icon} className={cx(styles.notificationImage)}alt='notification icon'/>
                  <div className={styles.notificationItem}>
                    {_.get(notification, 'title') && (<p style={{ width: 200 }} className={styles.title}>
                      <Text text={_.get(notification, "title")} lines={1} tooltip={{placement: "auto"}} />
                    </p>)}
                    <p className={styles.description}>{_.get(notification, 'message')}
                      {hasMore && <span className={styles.notificationCount}>+{notification.totalEvents - 1} more</span>}
                    </p>
                  </div>
                </div>
                <div className={styles.timeStamp}>
                  <TimeAgo date={_.get(notification, 'createdDate')} />
                </div>
              </li>
            );

            if (_.get(notification, 'url')) {
              return (
                <a key={key} className={styles.link} href={notification.url} target="_blank" rel="noopener noreferrer">
                  {notificationItem}
                </a>
              );
            }
            return notificationItem;
          })}
        </ul>
      )}
    </div>
  );
};

const NotificationSlider: React.FC<NotificationSliderProps> = ({ show, onClose }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);

  const { records, loading, nextPage, hasMoreItems, deleting, totalUnreadNotifications } = useSelector((state: any) => {
    const notifications = _.get(state, "notification", {});
    const loading = _.get(notifications, "loading", true);
    const pagination = _.get(notifications, "pagination", {});
    
    const totalPages = _.get(pagination, "totalPages", 0);
    let hasMoreItems = _.get(pagination, "hasMoreItems", false);
    
    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= totalPages) {
      nextPage = null;
    }

    const records = _.get(notifications, "records", []);
    const deleting = _.get(notifications, "deleting", false);
    const totalUnreadNotifications = _.filter(records, (notification) => !notification.read).length;

    return {records, loading, nextPage, hasMoreItems, deleting, totalUnreadNotifications};
  }, _.isEqual);

  const filteredNotifications = activeTab === 1 ? records : records.filter((notification: Notification) => !notification.read);
  const { trackNotificationListScreenView } = useHubAdoption();
  
  useEffect(() => {
    if(show){
      trackNotificationListScreenView();
    }
  },[show])

  const loadMoreNotifications = () => {
    if (loading || !hasMoreItems) {
      return; 
    }
    if (nextPage) {
      fetchNotificationsForPage(nextPage);
    }
  }; 

  const renderEmptyMessage = () => (
    <PlaceholderEmptyMessage
      className={styles.emptyMessage}
      title={<span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize('You are fully up-to-date with all prior messages') }} />}
      message=""
      image={NotificationEmptyMessage}
      visible={true}
    />
  );

  const renderNotifications = () => {
    const groupedNotifications = groupNotificationsByDate(filteredNotifications);
    return (
      <div className={styles.notificationsContainer}>
        {Object.keys(groupedNotifications).map((groupTitle) => {
          const notifications = groupedNotifications[groupTitle];
          return renderGroupedNotifications(notifications, groupTitle);
        })}
      </div>
    );
  };
  
  useEffect(() => {
    if(show){
      fetchNotificationsForPage(0);
    }
  }, [show]);

  const fetchNotificationsForPage = (page: number) => {
    const request = { page };
    dispatch(fetchNotifications(request));
  };

  const handleSelectionChange = (action: number) => {
    setActiveTab(action);

    if (action === 1) {
      dispatch(updatingNotificationsAsRead());
      fetchNotificationsForPage(0);
    }
    fetchNotificationsForPage(0);
  };

  const hasMoreNotifications = () => {
    if (activeTab === 0) {
      // check if the unread notifications are of smae length as the records
      return (totalUnreadNotifications >= _.size(records)) && hasMoreItems;
    }
    return hasMoreItems;
  };

  return (
    <Slider.Dialog width={"380"} show={show} onClose={onClose} className={styles.sliderBody} placement="end">
      <Slider.Header
        title={
          <div className={styles.notificationSlider}>
            notifications
            <i className={cx(styles.closeButton, "paperflite-close")} onClick={onClose} />
            <div className={styles.tabContainer}>
              <TabBar className={cx(styles.tabs)} gap={32} tabSize="equal" active={activeTab} onSelectionChange={(action) => handleSelectionChange(action)}>
                <div className={cx(styles.tab, { [styles.active]: activeTab === 0 })}>
                  Unread
                </div>
                <div className={cx(styles.tab, { [styles.active]: activeTab === 1 })}>
                  All
                </div>
              </TabBar>
            </div>
          </div>
        }
      />

      <Slider.Body className={styles.notificationSliderBody}>
        {!loading && _.size(filteredNotifications) === 0 && renderEmptyMessage()}
        <InfiniteScroll
          loading={loading}
          hasMore={hasMoreNotifications()}
          loadMore={loadMoreNotifications}
          loader={() => {
            if (_.size(filteredNotifications) === 0) {
              return (
                <div className={styles.NotificationPlaceholder}>
                  <NotificationPlaceholder width={'70%'} />
                  <NotificationPlaceholder width={'70%'} />
                  <NotificationPlaceholder width={'70%'} />
                </div>
              );
            } else {
              return <NotificationPlaceholder width={'70%'} />;
            }
          }}
        >
          {renderNotifications()}
        </InfiniteScroll>
      </Slider.Body>
      
      <div className={styles.actions}>
      {filteredNotifications.length > 0 && activeTab === 1 && (
          <div className={styles.deleteContainer}>
            <i className={cx("paperflite-delete", styles.deleteIcon, { [styles.deletedIcon]: deleting })} />
            <button
              className={styles.deleteButton}
              disabled={deleting}
              onClick={() => dispatch(deleteAllNotifications())}>
              {deleting ? `Deleting...` : `Delete all`}
            </button>
          </div>
      )}
      </div>
    </Slider.Dialog>
  );
};

export { NotificationSlider };

export const NotificationPlaceholder: React.FC<{ width: string }> = ({ width }) => {
  return (
    <div className={styles.placeholderContainer} style={{ width: width }}>
      <img className={styles.assetImgs} />
      <div className={styles.placeholderLines}>
        <div className={styles.line} style={{ width: '100%' }}></div>
        <div className={styles.line} style={{ width: '80%' }}></div>
      </div>
    </div>
  );
};

